import React, { useState, useEffect, useRef } from "react";
import Navbar from "../components/Navbar";

// ── Domino Etkisi — Görsel Domino Zinciri Simülasyonu ────────────────────────
// Konsept: Gerçek domino taşları zinciri — aynı şifre kullanılınca biri düşünce hepsi devrilir.
// Benzersiz mekanik: Şifreler arasındaki eşleşmeleri görsel olarak bağlayan animasyonlu zincir.

const PLATFORMS = [
  { id:1, name:"Oyun Sunucusu",    icon:"🎮", color:"#6366f1", x:60  },
  { id:2, name:"Sosyal Medya",     icon:"📱", color:"#f97316", x:195 },
  { id:3, name:"E-Posta",          icon:"📧", color:"#06b6d4", x:330 },
  { id:4, name:"Okul Portali",     icon:"🏫", color:"#8b5cf6", x:465 },
  { id:5, name:"Online Banka",     icon:"🏦", color:"#ef4444", x:600 },
];


export default function DominoSimulator() {
  const [passwords, setPasswords] = useState({ 1:"",2:"",3:"",4:"",5:"" });
  const [twoFA, setTwoFA] = useState({ 1:false,2:false,3:false,4:false,5:false });
  const [phase, setPhase] = useState("setup"); // setup | countdown | attack | done
  const [fallen, setFallen] = useState({});   // id -> "hacked"|"safe"|"2fa"|"checking"
  const [connections, setConnections] = useState([]); // [{from,to,type}]
  const [countdown, setCountdown] = useState(3);
  const [activeLog, setActiveLog] = useState([]);
  const [result, setResult] = useState(null);
  const logRef = useRef(null);

  useEffect(() => { if(logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight; }, [activeLog]);

  const addLog = (msg, color="#94a3b8") => setActiveLog(p => [...p, { msg, color, id: Date.now()+Math.random() }]);

  // Eşleşen şifre gruplarını bul
  const findMatches = () => {
    const groups = {};
    Object.entries(passwords).forEach(([id, pw]) => {
      if (!pw) return;
      if (!groups[pw]) groups[pw] = [];
      groups[pw].push(Number(id));
    });
    return Object.values(groups).filter(g => g.length > 1);
  };
  const matchGroups = findMatches();
  const duplicateIds = matchGroups.flat();

  const baslat = () => {
    setPhase("countdown");
    setCountdown(3);
  };

  useEffect(() => {
    if (phase !== "countdown") return;
    if (countdown <= 0) {
      setPhase("attack");
      runAttack();
      return;
    }
    const t = setTimeout(() => setCountdown(c => c-1), 1000);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, countdown]);

  const runAttack = () => {
    const firstPw = passwords[1] || "sifre123";
    setFallen({ 1:"hacked" });
    addLog(`💥 ${PLATFORMS[0].name} veri ihlali tespit edildi!`, "#ef4444");
    addLog(`🔑 Sızdırılan şifre: "${firstPw}"`, "#fbbf24");

    let newConnections = [];

    PLATFORMS.slice(1).forEach((p, idx) => {
      const delay = 1800 + idx * 2200;
      setTimeout(() => {
        setFallen(prev => ({ ...prev, [p.id]:"checking" }));
        addLog(`🤖 "${firstPw}" → ${p.name} deneniyor...`, "#94a3b8");

        setTimeout(() => {
          let status;
          if (passwords[p.id] !== firstPw && passwords[p.id] !== "") {
            status = "safe";
            addLog(`❌ ${p.name} — Farklı şifre, erişim reddedildi.`, "#86efac");
          } else if (twoFA[p.id]) {
            status = "2fa";
            addLog(`🛡️ ${p.name} — Şifre doğru ama 2FA engeli aşılamadı!`, "#60a5fa");
            newConnections = [...newConnections, { from:1, to:p.id, type:"blocked" }];
            setConnections([...newConnections]);
          } else {
            status = "hacked";
            addLog(`💀 ${p.name} — HESAP ELE GEÇİRİLDİ! Domino devrildi!`, "#ef4444");
            newConnections = [...newConnections, { from:1, to:p.id, type:"hacked" }];
            setConnections([...newConnections]);
          }
          setFallen(prev => ({ ...prev, [p.id]:status }));

          if (idx === PLATFORMS.length - 2) {
            setTimeout(() => {
              setPhase("done");
            }, 1000);
          }
        }, 900);
      }, delay);
    });
  };

  useEffect(() => {
    if (phase === "done") {
      const vals = Object.values(fallen);
      setResult({
        hacked: vals.filter(v => v === "hacked").length - 1,
        safe:   vals.filter(v => v === "safe").length,
        blocked: vals.filter(v => v === "2fa").length,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  const sifirla = () => {
    setPasswords({1:"",2:"",3:"",4:"",5:""});
    setTwoFA({1:false,2:false,3:false,4:false,5:false});
    setPhase("setup");
    setFallen({});
    setConnections([]);
    setActiveLog([]);
    setResult(null);
  };

  // Domino taşı rengi
  const dominoColor = (id) => {
    const s = fallen[id];
    if (s === "hacked")   return { bg:"rgba(239,68,68,0.2)",  brd:"#ef4444", shadow:"0 0 25px rgba(239,68,68,0.5)", tilt:true };
    if (s === "2fa")      return { bg:"rgba(59,130,246,0.18)", brd:"#60a5fa", shadow:"0 0 25px rgba(96,165,250,0.5)", tilt:false };
    if (s === "safe")     return { bg:"rgba(34,197,94,0.15)",  brd:"#22c55e", shadow:"0 0 20px rgba(34,197,94,0.4)",  tilt:false };
    if (s === "checking") return { bg:"rgba(251,191,36,0.12)", brd:"#fbbf24", shadow:"0 0 15px rgba(251,191,36,0.4)", tilt:false };
    const isDup = duplicateIds.includes(id) && phase === "setup";
    return isDup
      ? { bg:"rgba(239,68,68,0.08)", brd:"rgba(239,68,68,0.4)", shadow:"none", tilt:false }
      : { bg:"rgba(255,255,255,0.04)", brd:"rgba(255,255,255,0.12)", shadow:"none", tilt:false };
  };

  return (
    <div style={{ minHeight:"100vh",background:"linear-gradient(160deg,#07090f 0%,#0d1220 50%,#070c18 100%)",fontFamily:"'Sora',sans-serif",color:"#fff" }}>
      <Navbar />
      <style>{`
        @keyframes fadeUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}
        @keyframes tilt{0%{transform:rotate(0deg)}100%{transform:rotate(75deg) translateY(10px)}}
        @keyframes shake{0%,100%{transform:translateX(0)}25%{transform:translateX(-4px)}75%{transform:translateX(4px)}}
        @keyframes glow{0%,100%{opacity:0.6}50%{opacity:1}}
        @keyframes pulse{0%,100%{opacity:1}50%{opacity:0.4}}
        @keyframes countDown{from{transform:scale(0.5);opacity:0}to{transform:scale(1);opacity:1}}
      `}</style>

      <div style={{ maxWidth:"900px",margin:"0 auto",padding:"2rem" }}>

        {/* Başlık */}
        <div style={{ textAlign:"center",marginBottom:"2.5rem" }}>
          <div style={{ fontSize:"3rem",marginBottom:"0.5rem" }}>🁣</div>
          <h1 style={{ margin:0,fontSize:"2rem",fontWeight:"800",background:"linear-gradient(90deg,#f87171,#fb923c,#fbbf24)",WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent" }}>
            Domino Etkisi
          </h1>
          <p style={{ color:"#475569",marginTop:"0.4rem",fontSize:"0.88rem" }}>Bir şifre sızdırıldığında diğerleri nasıl devrilir?</p>
        </div>

        {/* Açıklama kartı */}
        {phase === "setup" && (
          <div style={{ background:"rgba(251,191,36,0.06)",border:"1px solid rgba(251,191,36,0.2)",borderRadius:"14px",padding:"1rem 1.5rem",marginBottom:"2rem",display:"flex",gap:"0.75rem",alignItems:"flex-start" }}>
            <span style={{ fontSize:"1.2rem",flexShrink:0 }}>💡</span>
            <div style={{ color:"#fde68a",fontSize:"0.85rem",lineHeight:"1.6" }}>
              <strong>Nasıl oynanır:</strong> Her platforma bir şifre gir. İlk platform (Oyun Sunucusu) mutlaka sızdırılacak. Diğerleri aynı şifreyi paylaşırsa devrilir! 2FA açık olanlar kurtulur.
              {duplicateIds.length > 0 && <span style={{ color:"#f87171",fontWeight:"700" }}> ⚠️ Aynı şifre tespit edildi — domino zinciri hazır!</span>}
            </div>
          </div>
        )}

        {/* Geri sayım */}
        {phase === "countdown" && (
          <div style={{ position:"fixed",inset:0,background:"rgba(0,0,0,0.85)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:50 }}>
            <div style={{ textAlign:"center",animation:"countDown 0.5s ease" }}>
              <div style={{ fontSize:"8rem",fontWeight:"900",color:"#ef4444",lineHeight:1 }}>{countdown || "🚨"}</div>
              <div style={{ color:"#94a3b8",fontSize:"1.2rem",marginTop:"0.5rem" }}>Saldırı başlıyor...</div>
            </div>
          </div>
        )}

        {/* Domino taşları — SVG bağlantılarla */}
        <div style={{ position:"relative",marginBottom:"2rem" }}>
          {/* SVG bağlantı çizgileri */}
          {connections.length > 0 && (
            <svg style={{ position:"absolute",top:0,left:0,width:"100%",height:"100%",pointerEvents:"none",overflow:"visible",zIndex:1 }}>
              {connections.map((conn,i) => {
                const fromP = PLATFORMS.find(p => p.id === conn.from);
                const toP   = PLATFORMS.find(p => p.id === conn.to);
                if (!fromP || !toP) return null;
                const color = conn.type === "hacked" ? "#ef4444" : "#60a5fa";
                return (
                  <g key={i}>
                    <line x1={fromP.x+50} y1={90} x2={toP.x+50} y2={90}
                      stroke={color} strokeWidth="2" strokeDasharray="6 4"
                      style={{ animation:"glow 1.5s infinite" }} />
                    <circle cx={toP.x+50} cy={90} r="5" fill={color} style={{ animation:"glow 1.5s infinite" }} />
                  </g>
                );
              })}
            </svg>
          )}

          {/* Platform kartları */}
          <div style={{ display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:"1rem",position:"relative",zIndex:2 }}>
            {PLATFORMS.map((p, idx) => {
              const { bg, brd, shadow, tilt } = dominoColor(p.id);
              const isFirst = idx === 0;
              return (
                <div key={p.id}
                  style={{ background:bg,border:`2px solid ${brd}`,borderRadius:"16px",padding:"1.25rem 0.75rem",textAlign:"center",
                    boxShadow:shadow,transition:"all 0.5s ease",
                    animation: fallen[p.id]==="hacked" ? "shake 0.3s ease" : fallen[p.id]==="checking" ? "pulse 0.8s infinite" : "none",
                    transform: tilt ? "rotate(25deg) translateY(8px)" : "none",
                  }}>
                  {/* Domino taşı görünümü */}
                  <div style={{ fontSize:"1.6rem",marginBottom:"0.35rem" }}>{p.icon}</div>
                  <div style={{ fontWeight:"700",fontSize:"0.78rem",marginBottom:"0.6rem",lineHeight:"1.3" }}>{p.name}</div>

                  {/* Durum etiketi */}
                  {fallen[p.id] && (
                    <div style={{ fontSize:"0.72rem",fontWeight:"700",marginBottom:"0.6rem",
                      color:fallen[p.id]==="hacked"?"#f87171":fallen[p.id]==="2fa"?"#60a5fa":fallen[p.id]==="safe"?"#86efac":"#fbbf24" }}>
                      {fallen[p.id]==="hacked"?"💀 Devrildi":fallen[p.id]==="2fa"?"🛡️ 2FA Engeli":fallen[p.id]==="safe"?"✅ Güvende":"⏳ Deneniyor..."}
                    </div>
                  )}

                  {/* Şifre girişi */}
                  {phase === "setup" && (
                    <input
                      placeholder={isFirst ? "Sızdırılacak..." : "Şifre gir..."}
                      value={passwords[p.id]}
                      onChange={e => setPasswords(prev => ({ ...prev, [p.id]:e.target.value }))}
                      style={{ width:"100%",boxSizing:"border-box",background:"rgba(0,0,0,0.4)",border:`1px solid ${duplicateIds.includes(p.id)?"#f87171":"rgba(255,255,255,0.12)"}`,borderRadius:"8px",padding:"0.4rem 0.5rem",color:"#fff",fontSize:"0.75rem",outline:"none",marginBottom:"0.5rem",fontFamily:"monospace",textAlign:"center" }}
                    />
                  )}
                  {phase !== "setup" && (
                    <div style={{ fontFamily:"monospace",fontSize:"0.72rem",color:"#475569",background:"rgba(0,0,0,0.3)",borderRadius:"6px",padding:"0.3rem 0.4rem",marginBottom:"0.5rem",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>
                      {passwords[p.id] || "—"}
                    </div>
                  )}

                  {/* 2FA toggle — ilk platform hariç */}
                  {!isFirst && (
                    <button
                      disabled={phase !== "setup"}
                      onClick={() => setTwoFA(prev => ({ ...prev, [p.id]:!prev[p.id] }))}
                      style={{ width:"100%",padding:"0.35rem",borderRadius:"7px",border:"none",fontSize:"0.7rem",fontWeight:"700",cursor:phase!=="setup"?"not-allowed":"pointer",transition:"all .2s",
                        background:twoFA[p.id]?"rgba(59,130,246,0.3)":"rgba(255,255,255,0.06)",
                        color:twoFA[p.id]?"#93c5fd":"#475569" }}>
                      {twoFA[p.id]?"🛡️ 2FA":"🔓 2FA?"}
                    </button>
                  )}
                  {isFirst && <div style={{ fontSize:"0.68rem",color:"#ef4444",fontWeight:"600",marginTop:"0.25rem" }}>⚡ İlk hedef</div>}
                </div>
              );
            })}
          </div>
        </div>

        {/* Aynı şifre uyarısı */}
        {phase === "setup" && duplicateIds.length > 0 && (
          <div style={{ background:"rgba(239,68,68,0.1)",border:"1px solid rgba(239,68,68,0.35)",borderRadius:"12px",padding:"0.75rem 1.25rem",marginBottom:"1.5rem",textAlign:"center",animation:"fadeUp .4s ease" }}>
            <span style={{ color:"#fca5a5",fontWeight:"600",fontSize:"0.88rem" }}>
              ⚠️ {matchGroups.map(g => `${g.length} hesap aynı şifreyi`).join(", ")} kullanıyor — Domino zinciri oluştu!
            </span>
          </div>
        )}

        {/* Başlat */}
        {phase === "setup" && (
          <div style={{ textAlign:"center",marginBottom:"2rem" }}>
            <button onClick={baslat}
              style={{ background:"linear-gradient(135deg,#ef4444,#f97316)",border:"none",borderRadius:"16px",padding:"1rem 3.5rem",fontSize:"1.1rem",fontWeight:"800",color:"#fff",cursor:"pointer",boxShadow:"0 8px 30px rgba(239,68,68,0.4)",letterSpacing:"0.02em" }}>
              ⚡ Domino Saldırısını Başlat
            </button>
          </div>
        )}

        {/* Log konsolu */}
        {activeLog.length > 0 && (
          <div ref={logRef} style={{ background:"#000",border:"1px solid #1a2234",borderRadius:"12px",padding:"1rem",fontFamily:"monospace",fontSize:"0.8rem",maxHeight:"180px",overflowY:"auto",marginBottom:"1.5rem" }}>
            <div style={{ color:"#ef4444",fontWeight:"700",marginBottom:"0.5rem",fontSize:"0.75rem",letterSpacing:"0.05em" }}>▶ SALDIRI KONSOLU</div>
            {activeLog.map(l => <div key={l.id} style={{ color:l.color,marginBottom:"3px" }}>{l.msg}</div>)}
          </div>
        )}

        {/* Sonuç */}
        {phase === "done" && result && (
          <div style={{ background:"linear-gradient(135deg,rgba(10,10,20,0.95),rgba(15,20,35,0.95))",border:`2px solid ${result.hacked===0?"rgba(34,197,94,0.4)":"rgba(239,68,68,0.4)"}`,borderRadius:"22px",padding:"2rem",textAlign:"center",animation:"fadeUp .6s ease" }}>
            <div style={{ fontSize:"3rem",marginBottom:"0.75rem" }}>{result.hacked===0?"🏆":"⚠️"}</div>
            <h2 style={{ margin:"0 0 1.25rem",color:"#e2e8f0",fontSize:"1.4rem" }}>Domino Zinciri Sona Erdi</h2>
            <div style={{ display:"flex",justifyContent:"center",gap:"2.5rem",marginBottom:"1.5rem" }}>
              <div><div style={{ fontSize:"2.8rem",fontWeight:"900",color:"#f87171" }}>{result.hacked}</div><div style={{ color:"#64748b",fontSize:"0.8rem",marginTop:"0.2rem" }}>Devrildi</div></div>
              <div><div style={{ fontSize:"2.8rem",fontWeight:"900",color:"#86efac" }}>{result.safe}</div><div style={{ color:"#64748b",fontSize:"0.8rem",marginTop:"0.2rem" }}>Güvende</div></div>
              <div><div style={{ fontSize:"2.8rem",fontWeight:"900",color:"#60a5fa" }}>{result.blocked}</div><div style={{ color:"#64748b",fontSize:"0.8rem",marginTop:"0.2rem" }}>2FA Kurtardı</div></div>
            </div>
            <p style={{ color:"#94a3b8",lineHeight:"1.7",marginBottom:"1.5rem",maxWidth:"480px",margin:"0 auto 1.5rem" }}>
              {result.hacked === 0
                ? "🎉 Mükemmel! Farklı şifreler ve 2FA sayesinde domino zincirini kırdın."
                : `${result.hacked} hesabın art arda devrildi. Aynı şifre, saldırganın en güçlü silahıdır.`}
            </p>
            <button onClick={sifirla} style={{ background:"rgba(255,255,255,0.08)",border:"1px solid rgba(255,255,255,0.15)",borderRadius:"12px",padding:"0.75rem 2rem",color:"#fff",fontSize:"0.95rem",fontWeight:"600",cursor:"pointer" }}>
              🔄 Tekrar Dene
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
